/**
 * SellerGuard — OPTIMISTIC route guard for the seller dashboard (EPIC-006
 * Phase 7, story 006-011 AC#2).
 *
 * Unlike `AdminGuard`, this guard is AUTH-ONLY. Seller status is gated on the
 * server by OWNERSHIP (ADR-035: a seller is NOT an RBAC role), so the client
 * cannot cheaply decide "is this user a seller" without an extra round-trip.
 * Instead the guard renders the dashboard for any authenticated user and lets
 * the page handle the API's `403 forbidden.not_a_seller` → redirect `/account`
 * + toast (see `ProductListPage`). This keeps the happy path one render with no
 * blocking `/me` fetch.
 *
 * Paths:
 *   1. Authenticated (token in memory)        → <Outlet/>
 *   2. No token + boot refresh still in flight → brief pending spinner
 *   3. No token + boot refresh done            → <Navigate to="/seller/login?return_to=…">
 *
 * SCRUM-145 (Track 0 persona-auth Task 6): case 3 goes to the SELLER door.
 * Sending a seller to the consumer `/login` is the same wrong-door bug the
 * admin guard had — the personas have separate front doors.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { loginDoorForPath } from "@/features/auth/destination";
import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
} from "@/features/seller-dashboard/api";
import {
  selectAuthBootstrapped,
  selectIsAuthenticated,
  useAuthStore,
} from "@/lib/auth-store";

export function SellerGuard(): ReactElement {
  registerSellerDashboardI18n();
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const bootstrapped = useAuthStore(selectAuthBootstrapped);
  const location = useLocation();

  // ── Case 1: authenticated → render (optimistic; page handles 403). ────────
  if (isAuthenticated) {
    return <Outlet />;
  }

  // ── Case 2: boot refresh still in flight → pending. ───────────────────────
  if (!bootstrapped) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={t("loading")}
        data-testid="seller-guard-loading"
        className="flex min-h-screen w-full items-center justify-center"
      >
        <span
          aria-hidden="true"
          className="size-8 animate-spin rounded-full border-2 border-[var(--g-hairline)] border-t-[var(--g-primary)]"
        />
      </div>
    );
  }

  // ── Case 3: genuinely unauthenticated → the seller door. ──────────────────
  const returnTo = location.pathname + location.search;
  const params = new URLSearchParams({ return_to: returnTo });
  return <Navigate to={loginDoorForPath(location.pathname) + "?" + params.toString()} replace />;
}

export default SellerGuard;
